/**
 * Lógica pura dos destinos (Instagram…) que a tela do canal e a aba
 * "Destinos" do Scheduler dividem: o estado da conta conectada, se o destino
 * pode ser ligado e a sugestão da "Marca no vídeo". Fica fora dos componentes
 * para ser testada sem DOM. Ver apps/backend/docs/plan-platform-destinations.md.
 */
import type { ChipVariant } from '@/components/ui/Chip'
import type { DestinationAccount, InstagramDestination } from '@/api/endpoints/destinations'

const DAY_MS = 86_400_000

export const PLATFORM_LABELS = { youtube: 'YouTube', instagram: 'Instagram', tiktok: 'TikTok' } as const

// ---------------------------------------------------------------------------
// Conta conectada
// ---------------------------------------------------------------------------

/** ``@perfil`` para exibir, venha o nome com ou sem ``@``. */
export function displayHandle(username: string): string {
  const bare = username.trim().replace(/^@+/, '')
  return bare ? `@${bare}` : ''
}

/** O @ para comparar entre plataformas: sem ``@`` e em minúsculas. */
export function normalizeHandle(handle: string | null | undefined): string {
  return (handle ?? '').trim().replace(/^@+/, '').toLowerCase()
}

const ACCOUNT_TYPES: Record<string, string> = {
  BUSINESS: 'Empresa',
  MEDIA_CREATOR: 'Criador',
  CREATOR: 'Criador',
  PERSONAL: 'Pessoal',
}

/** "Empresa", "Criador"… — o tipo como o Instagram o devolve, em português. */
export function accountTypeLabel(type: string): string {
  return ACCOUNT_TYPES[type.toUpperCase()] ?? type
}

export const ACCOUNT_STATUS: Record<DestinationAccount['status'], { label: string; variant: ChipVariant }> = {
  active: { label: 'Conectado', variant: 'success' },
  expired: { label: 'Credencial expirada', variant: 'warning' },
  revoked: { label: 'Acesso revogado', variant: 'danger' },
}

function msUntil(expiresAt: string | null | undefined, now: Date): number | null {
  if (!expiresAt) return null
  const at = new Date(expiresAt).getTime()
  return Number.isNaN(at) ? null : at - now.getTime()
}

/** Dias até ``expiresAt``, arredondados (negativo depois de vencer), ou ``null`` sem data. */
export function daysUntil(expiresAt: string | null | undefined, now: Date = new Date()): number | null {
  const ms = msUntil(expiresAt, now)
  return ms === null ? null : Math.round(ms / DAY_MS)
}

/** "expira em 52 dias", "expira em 1 dia", "expira em menos de um dia", "expirou" — ou ``''``. */
export function expiryText(expiresAt: string | null | undefined, now: Date = new Date()): string {
  const ms = msUntil(expiresAt, now)
  if (ms === null) return ''
  if (ms <= 0) return 'expirou'
  if (ms < DAY_MS) return 'expira em menos de um dia'
  const days = Math.round(ms / DAY_MS)
  return days === 1 ? 'expira em 1 dia' : `expira em ${String(days)} dias`
}

/**
 * A credencial do Instagram vale 60 dias e o Prism renova sozinho quando faltam
 * menos de 30. Faltando uma semana, a renovação falhou: vale avisar.
 */
export const EXPIRY_WARNING_DAYS = 7

export function expiresSoon(expiresAt: string | null | undefined, now: Date = new Date()): boolean {
  const days = daysUntil(expiresAt, now)
  return days !== null && days <= EXPIRY_WARNING_DAYS
}

// ---------------------------------------------------------------------------
// Pode ligar o destino?
// ---------------------------------------------------------------------------

export type BlockReason = 'no_publisher' | 'publisher_off' | 'no_account' | 'account_expired' | 'account_revoked'

/**
 * Por que o destino não pode ser ligado agora, ou ``null`` quando pode. A ordem
 * é a do que o dono resolve primeiro: sem adaptador no servidor nada adianta;
 * sem conta, o caminho é a tela do canal.
 */
export function blockReason(dest: Pick<InstagramDestination, 'publisher' | 'account'>): BlockReason | null {
  if (!dest.publisher.registered) return 'no_publisher'
  if (!dest.account) return 'no_account'
  if (dest.account.status === 'expired') return 'account_expired'
  if (dest.account.status === 'revoked') return 'account_revoked'
  if (!dest.publisher.enabled) return 'publisher_off'
  return null
}

/** O flag de publicação de cada plataforma, com o nome do `config/settings.py` do backend. */
const PUBLISH_FLAGS: Record<string, string> = {
  Instagram: 'INSTAGRAM_PUBLISH_ENABLED',
}

/** O texto do motivo, para o cartão do destino. */
export function blockReasonText(reason: BlockReason, platformLabel = 'Instagram'): string {
  switch (reason) {
    case 'no_publisher':
      return `Este servidor ainda não sabe publicar no ${platformLabel}. O destino fica desligado até o deploy do adaptador.`
    case 'publisher_off': {
      // O motivo é o flag do servidor, e não a máquina: o prism-server também cai
      // aqui enquanto o flag estiver desligado. Por desenho, só ele o liga.
      const flag = PUBLISH_FLAGS[platformLabel]
      return (
        `A publicação no ${platformLabel} está desligada neste servidor` +
        (flag ? ` (${flag}, que só se liga no prism-server)` : '') +
        '. A configuração fica salva, e o destino pode ser ligado depois que o flag for ligado.'
      )
    }
    case 'no_account':
      return `Conecte a conta do ${platformLabel} na tela do canal para ligar este destino.`
    case 'account_expired':
      return `A credencial do ${platformLabel} expirou. Reconecte a conta na tela do canal; enquanto isso, nada é publicado.`
    case 'account_revoked':
      return `O ${platformLabel} revogou o acesso do Prism. Reconecte a conta na tela do canal; enquanto isso, nada é publicado.`
  }
}

/**
 * Motivos do disjuntor que o adaptador do Instagram usa: os códigos da Graph API
 * e o da legenda (`caption_missing`, dois Reels seguidos sem a legenda enviada).
 */
const BREAKER_LABELS: Record<string, string> = {
  '2207050': 'o Instagram restringiu a conta',
  '2207051': 'o Instagram marcou a publicação como spam',
  '190': 'a credencial deixou de valer',
  caption_missing: 'dois Reels seguidos saíram sem a legenda',
}

/** "o Instagram restringiu a conta" — ou o próprio código, quando ele é novo. */
export function breakerLabel(code: string): string {
  return BREAKER_LABELS[code] ?? `código ${code}`
}

/** O que conferir antes de religar, conforme o motivo do disjuntor. */
export function breakerAdvice(code: string): string {
  if (code === 'caption_missing') {
    return 'Confira a legenda dos últimos Reels no perfil. Ao religar, a contagem recomeça do zero.'
  }
  if (code === '190') return 'Reconecte a conta na tela do canal antes de religar.'
  return 'Confira o Status da conta no Instagram antes de religar.'
}

// ---------------------------------------------------------------------------
// Sugestão da "Marca no vídeo"
// ---------------------------------------------------------------------------

export interface BrandSuggestionInput {
  /** ``video_brand_mode`` salvo; ``undefined`` quando o backend não conhece o campo. */
  videoBrandMode: string | undefined
  /** O @ que vai no vídeo hoje (o ``channel_tag`` do canal, do YouTube). */
  primaryHandle: string | null | undefined
  /** As contas conectadas nos destinos. */
  accounts: ReadonlyArray<{ platform: keyof typeof PLATFORM_LABELS; username: string }>
}

export interface BrandSuggestion {
  primaryHandle: string
  platformLabel: string
  otherHandle: string
}

/**
 * Quando o vídeo grava o @ do YouTube e outra plataforma conectada tem outro @
 * (comparados sem ``@`` e em minúsculas), o @ do vídeo aponta para o lugar
 * errado numa delas: vale sugerir "nome do canal". Nos modos "nome" e "nada"
 * não há o que sugerir, e com o backend antigo a troca nem seria gravada.
 */
export function brandSuggestion(input: BrandSuggestionInput): BrandSuggestion | null {
  if (input.videoBrandMode !== 'handle') return null
  const primary = normalizeHandle(input.primaryHandle)
  if (!primary) return null
  const other = input.accounts.find((a) => {
    const handle = normalizeHandle(a.username)
    return handle !== '' && handle !== primary
  })
  if (!other) return null
  return {
    primaryHandle: displayHandle(input.primaryHandle ?? ''),
    platformLabel: PLATFORM_LABELS[other.platform],
    otherHandle: displayHandle(other.username),
  }
}

export function brandSuggestionText(s: BrandSuggestion): string {
  return (
    `O @ do YouTube (${s.primaryHandle}) é diferente do ${s.platformLabel} (${s.otherHandle}). ` +
    'Como o vídeo é o mesmo nas duas plataformas, o @ gravado nele aponta para o lugar errado numa delas. ' +
    'Recomendo gravar o nome do canal, sem @. Vale para os vídeos novos.'
  )
}
